import { Box, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, Typography, TextField } from '@mui/material'
import { VisibilityOffTwoTone, VisibilityTwoTone } from '@mui/icons-material'
import React, { useMemo, useState } from 'react'
import { passwordErrors, passwordValidators } from '../../schema'
import { useStyles } from '../../styles'
import CircleIcon from '@mui/icons-material/Circle'
import PasswordRequirements from './passwordRequirements'

const PasswordGuidance = ({ hasError, guidanceList }) => {
  const classes = useStyles()

  return (
    <Box className={classes.guidanceContainer}>
      <List sx={{ paddingLeft: 0, paddingBottom: 0, paddingTop: 0 }}>
        {guidanceList.map(({ passed, message }, index) => {
          const icon = passed
            ? <CircleIcon sx={{ marginRight: 1, width: '8px' }} htmlColor='green' />
            : <CircleIcon sx={{ marginRight: 1, width: '8px' }} htmlColor='red' />

          return (
            <ListItem sx={{ paddingLeft: 0, paddingBottom: 0, paddingTop: 0 }} key={index}>
              <ListItemIcon sx={{ minWidth: '30px' }}>{icon}</ListItemIcon>
              <ListItemText sx={{ m: 0 }}>{message}</ListItemText>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}

const PasswordStrength = ({ guidanceList, password, passwordStrength }) => {
  const classes = useStyles()

  const filledColor = passwordStrength <= 2 ? 'red' : 'green'
  const passwordStrengthLabels = ['Very Weak', 'Weak', 'Good', 'Great👌']

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' flexDirection='row' className={classes.passwordStrengthContainer}>
        {[1, 2, 3, 4].map((k, idx) => (
          <Box
            key={idx}
            className={classes.passwordStrengthItem}
            style={{ background: k <= passwordStrength && filledColor }}
          />
        ))}
      </Box>
      <Box textAlign='right' mt={1} pr={2}>
        <Typography style={{ fontSize: '12px', color: filledColor }}>{passwordStrengthLabels[passwordStrength - 1]}</Typography>
      </Box>
    </Box>

  )
}

const PasswordInput = ({
  field,
  fieldState,
  required = true,
  disabled = false,
  styleClasses
}) => {
  const password = field.value

  const handleMouseDownPassword = (event) => {
    event.preventDefault()
  }

  const [inputType, setInputType] = useState('password')

  const [hasError, guidanceList, passwordStrength] = useMemo(() => {
    const guidanceList = [
      {
        message: passwordErrors.LOWER_CHARACTER,
        passed: passwordValidators.LOWER_CHARACTER(password)
      },
      {
        message: passwordErrors.UPPER_CHARACTER,
        passed: passwordValidators.UPPER_CHARACTER(password)
      },
      {
        message: passwordErrors.DIGIT,
        passed: passwordValidators.DIGIT(password)
      },
      {
        message: passwordErrors.SPECIAL_CHARACTER,
        passed: passwordValidators.SPECIAL_CHARACTER(password)
      },
      {
        message: passwordErrors.MIN_LENGTH,
        passed: password.length >= 8
      }
    ]

    const getPasswordStrength = () => {
      const passedCount = guidanceList.filter(e => e.passed).length

      if (passedCount === guidanceList.length && password.length >= 12) return 4
      if (passedCount === guidanceList.length) return 3
      if (passedCount >= 3) return 2
      return 1
    }

    const passwordStrength = getPasswordStrength()
    const hasErrors = guidanceList.some(e => !e.passed)

    return [hasErrors, guidanceList, passwordStrength]
  }, [password])

  const PasswordInputTypeToggler = () => {
    const Icon = () => inputType === 'password' ? <VisibilityOffTwoTone /> : <VisibilityTwoTone />
    const toggleInputType = () => setInputType(inputType === 'password' ? 'text' : 'password')

    return (
      <InputAdornment position='end'>
        <IconButton
          aria-label='toggle password visibility'
          onClick={toggleInputType}
          onMouseDown={handleMouseDownPassword}
          edge='end'
          size='large'
        >
          <Icon />
        </IconButton>
      </InputAdornment>
    )
  }

  return (
    <Box display='flex' flexDirection='column'>
      <TextField
        label='Password'
        variant='outlined'
        fullWidth
        required={required}
        disabled={disabled}
        type={inputType}
        className={styleClasses}
        InputProps={{
          endAdornment: <PasswordInputTypeToggler />
        }}
        error={!!hasError && !!password.length}
        {...field}
      />
      {password.length
        ? (
          <>
            <PasswordStrength
              password={field.value}
              guidanceList={guidanceList}
              passwordStrength={passwordStrength}
            />
            <PasswordGuidance
              hasError={hasError}
              guidanceList={guidanceList}
            />
          </>
          )
        : <PasswordRequirements />}
    </Box>
  )
}

export default PasswordInput
